import { useState, type FormEvent } from 'react';
import { authOptions, loginMicrosoft, loginWithPassword, verifyLoginCode } from '../auth';
import { api } from '../api';

export function LoginPage() {
  const opts = authOptions();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [forgot, setForgot] = useState(false);
  const [step, setStep] = useState<{ challenge: string; email: string } | null>(null);
  const [code, setCode] = useState('');
  const [forgotSent, setForgotSent] = useState(false);

  async function sendForgot(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await fetch('/api/auth/forgot', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email: email.trim() }) });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error ?? 'Cererea nu a putut fi trimisă');
      setForgotSent(true);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      setStep(await loginWithPassword(email.trim(), password));
      setPassword('');
    } catch (err) {
      setError((err as Error).message);
    }
    setBusy(false);
  }

  async function submitCode(e: FormEvent) {
    e.preventDefault();
    if (!step) return;
    setBusy(true);
    setError(null);
    try {
      await verifyLoginCode(step.challenge, code);
      window.location.assign('/');
    } catch (err) {
      setError((err as Error).message);
      setBusy(false);
    }
  }

  return (
    <div className="login-page">
      <img className="login-watermark" src="/logo-mark.png" alt="" aria-hidden="true" />
      <div className="login-card">
        <img className="login-logo" src="/logo.png" alt="Moro Group" />

        <button className="microsoft-btn" onClick={() => loginMicrosoft()}>
          <MicrosoftIcon /> Continuă cu contul Microsoft
        </button>

        {opts.password && forgot && (
          <>
            <div className="divider"><span>resetare parolă</span></div>
            {forgotSent ? (
              <p>Dacă adresa are cont în aplicație, veți primi în câteva minute un e-mail cu linkul de resetare (valabil 2 ore).</p>
            ) : (
              <form onSubmit={sendForgot} className="login-form">
                <label>E-mail
                  <input type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} required autoFocus />
                </label>
                {error && <p className="error">{error}</p>}
                <button className="accent" type="submit" disabled={busy || !email}>{busy ? 'Se trimite…' : 'Trimite linkul de resetare'}</button>
              </form>
            )}
            <button className="link small" onClick={() => { setForgot(false); setForgotSent(false); setError(null); }}>Înapoi la autentificare</button>
          </>
        )}

        {opts.password && !forgot && step && (
          <>
            <div className="divider"><span>cod de verificare</span></div>
            <form onSubmit={submitCode} className="login-form">
              <p>Am trimis un cod de 6 cifre la <strong>{step.email}</strong>. Verificați și folderul Spam.</p>
              <label>Cod
                <input className="code-input" inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} autoFocus required />
              </label>
              {error && <p className="error">{error}</p>}
              <button className="accent" type="submit" disabled={busy || code.length !== 6}>{busy ? 'Se verifică…' : 'Confirmă'}</button>
            </form>
            <button className="link small" onClick={() => { setStep(null); setCode(''); setError(null); }}>Înapoi</button>
          </>
        )}

        {opts.password && !forgot && !step && (
          <>
            <div className="divider"><span>sau cu e-mail și parolă</span></div>
            <form onSubmit={submit} className="login-form">
              <label>E-mail
                <input type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} required />
              </label>
              <label>Parolă
                <input type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required />
              </label>
              {error && <p className="error">{error}</p>}
              <button className="accent" type="submit" disabled={busy || !email || !password}>
                {busy ? 'Se verifică…' : 'Autentificare'}
              </button>
            </form>
            <button className="link small" onClick={() => { setForgot(true); setError(null); }}>Ați uitat parola?</button>
          </>
        )}
      </div>
    </div>
  );
}

function MicrosoftIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 21 21" aria-hidden="true">
      <rect x="1" y="1" width="9" height="9" fill="#f25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
      <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
      <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
    </svg>
  );
}

/** Ecran obligatoriu după login cu parolă temporară. */
export function ChangePasswordPage({ onDone, forced }: { onDone: () => void; forced?: boolean }) {
  return (
    <div className="login-page">
      <img className="login-watermark" src="/logo-mark.png" alt="" aria-hidden="true" />
      <div className="login-card">
        <img className="login-logo" src="/logo.png" alt="Moro Group" />
        {forced && <p>Pentru siguranță, alegeți o parolă nouă înainte de a continua.</p>}
        <ChangePasswordForm onDone={onDone} />
      </div>
    </div>
  );
}

export function ChangePasswordForm({ onDone }: { onDone: () => void }) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [repeat, setRepeat] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (next !== repeat) return setError('Parolele noi nu coincid.');
    setBusy(true);
    setError(null);
    try {
      await api.post('/auth/change-password', { currentPassword: current, newPassword: next });
      onDone();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className="login-form">
      <label>Parola actuală<input type="password" autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.target.value)} required /></label>
      <label>Parola nouă<input type="password" autoComplete="new-password" value={next} onChange={(e) => setNext(e.target.value)} required /></label>
      <label>Repetați parola nouă<input type="password" autoComplete="new-password" value={repeat} onChange={(e) => setRepeat(e.target.value)} required /></label>
      <p className="muted small">Minimum 10 caractere, cu litere și cifre.</p>
      {error && <p className="error">{error}</p>}
      <button className="accent" type="submit" disabled={busy}>Schimbă parola</button>
    </form>
  );
}
